import { FixedEsiaRole, LoginOptions, LoginService } from "./services/login.service";
import { FuncType, NamedEntityDto } from "approot/global/models/common.model";
import { Select2Config } from "approot/global/ui-components/select2.directive";
import { LoginDataService } from "./services/login.data.service";
import { InitSelect2Info, LoginSelect2ConfigService } from "./services/login.select2.config.service";
import { LoginStrategiesService } from "./services/login.strategies.service";
import { PasswordRecoveryService } from "./services/password.recovery.service";
import { IdentityProvider, LoginFormMode, LoginRepository } from "./login.repository";
import { LoginCookiesService } from "./services/login.cookies.service";

import "webroot/static/vendor/components/jquery.cookie.js";
import { LoginNoticeService } from "./services/login.notice.service";
import { SsoChooseRoleService } from "./services/sso-choose-role.service";
import { ChangeLanguageService } from "./services/changeLanguage.service";
import { getMobileAuthContext } from "./mobile.auth.context";

declare var angular: ng.IAngularStatic;

type NcRole = "parent" | "staff"

enum NcRoles {
	Parent = "parent",
	Staff = "staff"
}

export interface SchoolSearchInfo {
	id: number;
	name: string;
	provinceId: number;
	cityId: number;
	inn: string;
	ogrn: string;
	address: string;
	shortName: string;
}

class LoginFormController {
	public loginName: string = "";
	public password: string = "";
	public remember: boolean = false;
	public form: ng.IFormController;

	public mode: LoginFormMode = LoginFormMode.educOrg;

	public organization: NamedEntityDto;

	public educManagement: NamedEntityDto;

	public orgSettings: Select2Config;
	public emSettings: Select2Config;

	private loginOptions?: LoginOptions;
	private esiaMainAuth: boolean;

	// видимый / невидимый пароль
	public showPassword: boolean = false;
	// режим редактирования логина
	public editLoginName: boolean = false;
	// режим редактирования пароля
	public editPassword: boolean = false;

	public ready: boolean;
	public back: string;
	public mobile: boolean;
	public mobileAccessToken: string;
	public loginState: string;
	public idpBindUser: boolean;
	public schoolId?: number;
	public acr_values: Record<string, string | boolean>;
	public urlParams: any;

	public urlIncludeLogin: boolean = false;
	public isShowLogPass: boolean = false;
	private isEmVisible: boolean = true;
	private funcType: FuncType[];
	private DEFAULT_PROVIDER = IdentityProvider.Esia;
	public provider: IdentityProvider;

	/*@ngInject*/
	constructor(
		private loginDataService: LoginDataService,
		private loginService: LoginService,
		private loginStrategiesService: LoginStrategiesService,
		private ssoChooseRoleService: SsoChooseRoleService,
		private select2ConfigService: LoginSelect2ConfigService,
		private passwordRecoveryService: PasswordRecoveryService,
		private loginCookiesService: LoginCookiesService,
		private loginNoticeService: LoginNoticeService,
		private loginRepository: LoginRepository,
		private $location: ng.ILocationService,
		private $timeout: ng.ITimeoutService,
		private $q: ng.IQService,
		private changeLanguageService: ChangeLanguageService,
		public language: any
	) {
		this.init();

		this.urlIncludeLogin = this.$location.path().indexOf('-pass') !== -1;
		this.urlParams = this.$location.search();
		if(this.urlParams.acr_values) {
			this.acr_values = this.parseAcrValuesString(this.urlParams.acr_values)
		}

		let searchParams = new URLSearchParams(window.location.search);
		this.initAuthProvider(searchParams);
	}

	private initAuthProvider(request: URLSearchParams) {
		let ret = parseInt(request.get('provider'));
		this.provider = ret > 0 ? ret : this.DEFAULT_PROVIDER;
	}

	parseAcrValuesString(acrString: string):Record<string, string | boolean> {
		const acrPairs = acrString.split(' ')

		return acrPairs.reduce((acc, pair) => {
			const [key,value] = pair.split(':')
			if (key) {
				acc[key] = typeof value !== 'undefined' ? value : true
			}
			return acc
		}, {})
	}

	externalAcrAuth(role: NcRole) {
		if (role === NcRoles.Parent) {
			this.loginStrategiesService.loginWithEsiaProviderPermissOptions(this.provider, { esiaPermissions: true, esiaRole: FixedEsiaRole.Parents });
		}
		else if (role === NcRoles.Staff) {
			this.loginStrategiesService.loginWithEsiaProviderPermissOptions(this.provider, { esiaRole: FixedEsiaRole.AllStaffs });
		}
	}

	private async init() {
		await this.changeLanguageService.pageLanguageLoaded;
		this.funcType = this.loginDataService.loginData.viewData.loginPassAllowedFuncTypes;
		this.esiaMainAuth = this.loginDataService.loginData.viewData.esiaMainAuth;
		if(this.funcType && this.funcType.length > 0)
			this.isEmVisible = this.funcType.includes(FuncType.educMgr);
		this.initOrgSettings();
		this.initEmSettings();
		this.initFromQuery();

		if (this.acr_values?.nc_role && this.urlParams.client_id == "ees-identity") {
			const role = this.acr_values.nc_role;
			if (role === 'parent' || role === 'staff') {
				return this.externalAcrAuth(role)
			}
		}

		this.load();
	}

	private initOrgSettings() {
		this.orgSettings = this.select2ConfigService.configure(<any>{
			placeholder: this.language.Generic.Login.kSelectOrganization,
			templateResult: (item, container) => {
				return this.select2ConfigService.displayOrgResult(item, container);
			},
			language: {
				inputTooShort: () => {
					return this.language.Generic.Login.kEnterOrganizationNumberOrName;
				}
			},
			ajax: {
				url: "/webapi/schools/search",
				data: (params) => {
					let nameParts = params.term.split(" ").filter(i => i.length > 0);
					let query: any = {
						name: nameParts,
						funcType: this.funcType,
						withAddress: true
					}
					return query;
				},
				processResults: (data: SchoolSearchInfo[], query) => {
					let showMsgAboutLongListOrgsFound = query.term?.length < 10 && data?.length > 20;
					if (showMsgAboutLongListOrgsFound) {
						return this.processResults([{ id: -2, name: this.language.Generic.Login.kLongSearchOrganizationResult }]);
					}
					return this.processResults(data);
				},
				getInitOptionsFn: () => {
					return this.getAjaxInitOptionsFn(this.organization);
				}
			}
		});
	}

	private initEmSettings() {
		this.emSettings = this.select2ConfigService.configure(<any>{
			placeholder: this.language.Generic.Login.kSelectManagementOrgan,
			dropdownCssClass: "em-results__options",
			language: this.changeLanguageService.getPageLanguage(),
			ajax: {
				url: "/webapi/educmanagements/search",
				data: (params) => {
					let nameParts = params.term.split(" ").filter(i => i.length > 0);
					let query: any = {
						name: nameParts,
						funcType: this.funcType,
					}
					return query;
				},
				processResults: (data: NamedEntityDto[]) => {
					return this.processResults(data);
				},
				getInitOptionsFn: () => {
					return this.getAjaxInitOptionsFn(this.educManagement);
				}
			}
		});
	}

	private initFromQuery() {
		let searchParams = new URLSearchParams(window.location.search);

		this.back = searchParams.get('back');
		const mobileContext = getMobileAuthContext(searchParams);
		this.mobile = mobileContext.has('mobile');
		if (this.mobile) {
			this.mobileAccessToken = mobileContext.get('access_token');
		}

		this.loginState = searchParams.get('loginState');
		this.idpBindUser = searchParams.has('idpBindUser');

		if (searchParams.has('SCID')) {
			this.schoolId = parseInt(searchParams.get('SCID'));
		}
	}

	private processResults(data) {
		let mappedData = data.map(i => {
			let optionItem = { id: i.id, text: null, obj: i };

			if (i.hasOwnProperty("shortName")) {
				optionItem.text = i.shortName
			} else {
				optionItem.text = i.name
			}

			return optionItem;
		});

		return { results: mappedData };
	}

	private getAjaxInitOptionsFn(data) {
		let options = null;

		if (data) {
			// todo: тут так потому что не всегда NamedEntityDto приходит
			let opt = { id: data.id, text: data.name || data.text, obj: data };
			options = [opt];
		}

		return options ?? [];
	}

	private authContextStore() {
		if (this.loginState) {
			return this.loginService.getMobileInfo(this.loginState).then((info) => {
				this.mobile = info?.isMobile;
			});
		}
		if (this.mobile) {
			let formCollection: any = { mobile: "1" };
			if (this.mobileAccessToken) {
				formCollection.mobileAccessToken = this.mobileAccessToken;
			}
			return this.loginRepository.createLoginState(formCollection).then((loginState) => {
				this.loginState = loginState;
			});
		}
		return Promise.resolve();
	}

	private load() {
		const promises = [];
		const authContextReady = this.authContextStore();
		promises.push(authContextReady);
		const loginDataReady = this.loginDataService.load();
		promises.push(loginDataReady);

		this.$q.all(promises)
			.then(() => {
				return this.initLogin();
			}).then(() => {
				this.onReady();
			});
	}

	private initLogin() {
		let cookies = this.loginCookiesService.readTTSLogin();
		let settings = this.loginDataService.loginData.viewData;

		this.isShowLogPass = !settings.esiaMainAuth

		if (!this.urlIncludeLogin) {
			this.isShowLogPass = !settings.esiaMainAuth
		} else {
			this.isShowLogPass = true
		}

		if (!this.isShowLogPass) {
			return
		}

		if (this.mobile || !this.isEmVisible) {
			this.mode = LoginFormMode.educOrg;
			if(this.mobile)
				this.funcType = [FuncType.school];
		}
		else {
			this.mode = this.loginCookiesService.readForm();
		}

		let initData = Object.assign({}, cookies, { cacheVer: settings.cacheVer });
		// 36152
		if (this.schoolId) {
			initData = Object.assign(initData, { SCID: this.schoolId });
		}

		initData.funcType = this.funcType;

		let config = { params: initData, cache: true };

		let promises = [];
		if (settings.schoolLogin) {
			const orgInit = this.loginRepository.prepareAuthForm(config).then((response) => {
				this.organization = response.organization;
			});
			promises.push(orgInit);
		}

		if (settings.emLogin) {
			const emInit = this.loginRepository.prepareEmAuthForm(config).then((response) => {
				this.educManagement = response.educManagement;
			});
			promises.push(emInit);
		}
		return this.$q.all(promises);
	}

	private onReady() {
		this.loginOptions = this.loginDataService.loginData.loginOptions;
		if (this.loginState) {
			this.loginOptions.loginState = this.loginState;
		}
		if (this.idpBindUser) {
			this.loginOptions.idpBindUser = this.idpBindUser;
		}
		this.ready = true;
		// инициализация событий
		this.select2ConfigService.initEvents();
		this.initSelection();
		// уведомление
		this.loginNoticeService.noticeProcess();
		// событие полной готовности формы авторизации
		this.$timeout(() => {
			angular.element(document).trigger("loginform-ready");
		});
	}

	initSelection() {
		let initInfo: InitSelect2Info = null;

		if (this.mode == LoginFormMode.educOrg && this.organization) {
			initInfo = { selectName: "org", selectItem: this.organization };
		}
		else if (this.mode == LoginFormMode.em && this.educManagement) {
			initInfo = { selectName: "em", selectItem: this.educManagement };
		}
		this.select2ConfigService.initSelection(initInfo);
	}

	changeMode(mode: LoginFormMode) {
		this.mode = mode;
		this.loginCookiesService.writeForm(this.mode);
		this.initSelection();
	}

	canEsia(): boolean {
		return this.loginDataService.loginData.viewData?.esiaLogin;
	}

	canEsa(): boolean {
		return this.loginDataService.loginData.viewData?.esaLogin;
	}

	get productName(): string {
		let productName = this.loginDataService.loginData.viewData?.productName;
		return productName ? productName : this.language.Generic.ServAdmin.kNetCity;
	}

	goToAccounts() {
		this.$location.path("/authorize/accounts");
	}

	loginWithEsia() {
		this.ssoChooseRoleService.execute(IdentityProvider.Esia);
	}

	loginWithEsa() {
		this.ssoChooseRoleService.execute(IdentityProvider.Esa);
	}

	login() {
		if (this.form.$invalid) {
			this.form.$displayErrors = true;
			return;
		}

		let options = Object.assign({}, this.loginOptions, { saveAccount: this.remember });
		// 36152
		if (this.schoolId) {
			options.entryPage = this.loginOptions.redirectUri;
		}

		let schoolId = null;
		let emId = null;
		if (this.mode == LoginFormMode.em) {
			emId = this.educManagement.id;
		} else {
			schoolId = this.organization.id;
		}

		let addOpts: any = {};
		if (this.mobile) {
			addOpts.mobile = 1;
		}
		this.loginStrategiesService.loginWithLoginPassCheck(this.loginName, this.password, schoolId, emId, options, addOpts)
			.catch((reason) => {
				this.password = null;
			});
	}

	recoveryPassword() {
		this.passwordRecoveryService.goToPassRecovery();
	}

	toggleRemember() {
		this.remember = !this.remember;
	}

	togglePasswordIcon() {
		this.showPassword = !this.showPassword;
	}
}

export const LoginFormComponent = {
	controller: LoginFormController,
	controllerAs: "$ctrl",
	templateUrl: "/static/dist/app/login/loginform.component.html"
}
